import mixpanel from 'mixpanel-browser';
import Parse from 'parse';
import debounce from 'lodash/debounce';

class MixpanelTracker {
  private static instance: MixpanelTracker;
  private initialized = false;
  private token = '94d10c9a05bb04ac55869d43151b44f0';
  private eventBuffer: Array<{name: string; properties: any}> = [];
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private initPromise: Promise<void> | null = null;

  private constructor() {
    setInterval(() => this.flushEvents(), this.FLUSH_INTERVAL);
  }

  public static getInstance(): MixpanelTracker {
    if (!MixpanelTracker.instance) {
      MixpanelTracker.instance = new MixpanelTracker();
    }
    return MixpanelTracker.instance;
  }

  private async initializeOnce(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.initializeImpl();
    }
    return this.initPromise;
  }

  private getUserName(user: Parse.User): string {
    if (user.get('name')) {
      return user.get('name');
    }

    const firstName = user.get('firstName') || '';
    const lastName = user.get('lastName') || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return 'Guest';
  }

  private getUserEmail(user: Parse.User): string | undefined {
    return user.get('email') || user.get('verifiedEmail');
  }

  private getUserMobile(user: Parse.User): string | undefined {
    return user.get('MobileNumber') || user.get('verifiedMobileNumber');
  }

  private async initializeImpl(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      this.clearMixpanelState();

      mixpanel.init(this.token, {
        debug: false,
        track_pageview: false,
        persistence: 'localStorage',
        ignore_dnt: true,
        batch_requests: true,
        batch_size: 100,
        batch_flush_interval_ms: this.FLUSH_INTERVAL,
        cross_subdomain_cookie: false,
        secure_cookie: true,
      });

      const currentUser = Parse.User.current();
      if (currentUser) {
        await this.setUserIdentity(currentUser);
      }

      this.initialized = true;
    } catch (error) {
      console.error('Mixpanel initialization failed:', error);
      this.initialized = false;
      this.initPromise = null;
      throw error;
    }
  }

  private async setUserIdentity(user: Parse.User): Promise<void> {
    const userId = user.id;
    if (!userId) return;

    mixpanel.identify(userId);
    const userName = this.getUserName(user);
    const userEmail = this.getUserEmail(user);
    const userMobile = this.getUserMobile(user);

    const userProperties = {
      $email: userEmail,
      $name: userName,
      userId: userId,
      username: user.get('username'),
      mobileNumber: userMobile,
      firstSeen: new Date().toISOString(),
    };

    mixpanel.people.set_once(userProperties);
  }

  private clearMixpanelState(): void {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('mp_'))
        .forEach((key) => localStorage.removeItem(key));

      document.cookie.split(';').forEach((cookie) => {
        const [name] = cookie.split('=');
        if (name.trim().startsWith('mp_')) {
          document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
    } catch (error) {
      console.error('Error clearing Mixpanel state:', error);
    }
  }

  private flushEvents = debounce(
    async () => {
      if (!this.initialized || this.eventBuffer.length === 0) return;

      const events = [...this.eventBuffer];
      this.eventBuffer = [];

      events.forEach(({name, properties}) => {
        try {
          mixpanel.track(name, properties);
        } catch (error) {
          console.error(`Failed to track event ${name}:`, error);
        }
      });
    },
    1000,
    {maxWait: this.FLUSH_INTERVAL},
  );

  public async init(): Promise<void> {
    return this.initializeOnce();
  }

  public async track(
    eventName: string,
    properties: Record<string, any> = {},
  ): Promise<void> {
    try {
      if (!this.initialized) {
        await this.initializeOnce();
      }

      const user = Parse.User.current();
      const enhancedProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        userId: user?.id,
        userEmail: user ? this.getUserEmail(user) : undefined,
        username: user?.get('username'),
        userName: user ? this.getUserName(user) : 'Guest',
        mobileNumber: user ? this.getUserMobile(user) : undefined,
      };

      this.eventBuffer.push({
        name: eventName,
        properties: enhancedProperties,
      });

      this.flushEvents();
    } catch (error) {
      console.error(`Failed to queue event ${eventName}:`, error);
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }
}

export const ownersAnalytics = {
  trackLogin: (user: Parse.User) => {
    mixpanelTracker.track('Owners Login', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
    });
  },
  trackLogout: (user: Parse.User) => {
    mixpanelTracker.track('Owners Logout', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
      timestamp: new Date().toISOString(),
    });
  },
  trackOverview: (user: Parse.User, startDate: string, endDate: string) => {
    mixpanelTracker.track('Owners Overview', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
      startDate,
      endDate,
    });
  },
  trackCustomers: (user: Parse.User) => {
    mixpanelTracker.track('Owners Customers View', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
      timestamp: new Date().toISOString(),
    });
  },
  trackOrder: (user: Parse.User) => {
    mixpanelTracker.track('Owners Order', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
      timestamp: new Date().toISOString(),
    });
  },
  trackExportCSV: (user: Parse.User) => {
    mixpanelTracker.track('Owners Export CSV', {
      userId: user.id,
      userEmail: user.get('email'),
      userName: user.get('name'),
      mobileNumber:
        user.get('mobileNumber') || user.get('verifiedMobileNumber'),
      timestamp: new Date().toISOString(),
    });
  },
};
// export const orderAnalytics = {
//   trackPageView: (orderData: any) => {
//     mixpanelTracker.track('Order Page View', {
//       source: window.location.pathname,
//       referrer: document.referrer,
//       orderTotal: orderData?.total,
//       itemCount: orderData?.items?.length,
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackItemAction: (action: any, item: any, orderData: any) => {
//     mixpanelTracker.track('Order Item Action', {
//       action,
//       itemName: item.name,
//       itemPrice: item.price,
//       orderTotal: orderData?.total,
//       timestamp: new Date().toISOString(),
//     });

//     if (action === 'add') {
//       ReactPixel.track('AddToCart', {
//         content_name: item.name,
//         value: item.price,
//       });
//     } else if (action === 'remove') {
//       ReactPixel.track('RemoveFromCart', {
//         content_name: item.name,
//         value: item.price,
//       });
//     }
//   },

//   trackOrderSubmit: async (orderData: any, success: any, error: any) => {
//     const eventData = {
//       orderTotal: orderData.total,
//       itemCount: orderData.items?.length,
//       appetizersAsMainCourses: orderData.appetizersAsMainCourses,
//       isGroupOrder: orderData.isGroupOrder,
//       userId: orderData.userId,
//       userEmail: orderData.userEmail,
//       username: orderData.username,
//       mobileNumber: orderData.MobileNumber || orderData.verifiedMobileNumber,
//       restaurantId: orderData.restaurantId,
//       restaurantName: orderData.restaurantName,
//       action: success,
//       error: error || null,
//       timestamp: new Date().toISOString(),
//     };

//     await mixpanelTracker.track('Send To Kitchen', eventData);

//     if (success) {
//       ReactPixel.track('Purchase', {
//         value: orderData.total,
//         currency: orderData.currency,
//       });
//     } else {
//       ReactPixel.trackCustom('FailedOrder', {
//         value: orderData.total,
//         error,
//       });
//     }
//   },

//   trackPaymentMethod: (method: any, orderData: any) => {
//     mixpanelTracker.track('Payment Method Selected', {
//       method,
//       orderTotal: orderData.total,
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackConfirmOrderType: (orderType: any, orderData: any, entityName?: any) => {
//     mixpanelTracker.track('Order Type Selected', {
//       orderType,
//       orderId: orderData.id,
//       entityName: entityName,
//       timestamp: new Date().toISOString(),
//     });
//   },
//   trackPaymentGatewayResponse: (response: any, paymentLink: any) => {
//     mixpanelTracker.track('Payment Gateway Response', {
//       response,
//       paymentLink,
//       timestamp: new Date().toISOString(),
//     });
//   },
//   trackTipAmount: (amount: number, orderData: any, tipPercentage?: number) => {
//     mixpanelTracker.track('Tip Amount Selected', {
//       amount,
//       orderTotal: orderData.total,
//       tipPercentage: tipPercentage || (amount / orderData.total) * 100,
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackWaiterCall: () => {
//     mixpanelTracker.track('Waiter Called', {
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackWaiterCallButtonClick: () => {
//     mixpanelTracker.track('Waiter Call Button Click', {
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackWaiterCallSelection: (selection: string | string[]) => {
//     mixpanelTracker.track('Waiter Call Selection', {
//       selection,
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackWaiterCallCancel: () => {
//     mixpanelTracker.track('Waiter Call Cancelled', {
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackUserInteraction: (action: any, data = {}) => {
//     mixpanelTracker.track(action, {
//       ...data,
//       timestamp: new Date().toISOString(),
//     });
//   },
// };

// export const reviewAnalytics = {
//   trackReviewSubmit: (
//     reviews: any[],
//     comment: string,
//     success: boolean,
//     restaurant: any,
//     user: any,
//     error?: string,
//   ) => {
//     mixpanelTracker.track('Review Submit', {
//       reviews,
//       hasComment: !!comment,
//       success,
//       error: error || null,
//       restaurantId: restaurant?.id,
//       restaurantName: restaurant?.get('name'),
//       userId: user?.id,
//       userEmail: user?.get('email') || user?.get('verifiedEmail'),
//       userName:
//         user?.get('name') ||
//         `${user?.get('firstName')} ${user?.get('lastName')}`.trim(),
//       userMobile:
//         user?.get('MobileNumber') || user?.get('verifiedMobileNumber'),
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackReviewCancel: (restaurant: any, user: any) => {
//     mixpanelTracker.track('Review Cancelled', {
//       restaurantId: restaurant?.id,
//       restaurantName: restaurant?.get('name'),
//       userId: user?.id,
//       userEmail: user?.get('email') || user?.get('verifiedEmail'),
//       userName:
//         user?.get('name') ||
//         `${user?.get('firstName')} ${user?.get('lastName')}`.trim(),
//       userMobile:
//         user?.get('MobileNumber') || user?.get('verifiedMobileNumber'),
//       timestamp: new Date().toISOString(),
//     });
//   },
// };

// export const spinTheWheelAnalytics = {
//   trackWheelSpin: (
//     success: boolean,
//     prize: any,
//     restaurant: any,
//     user: any,
//   ) => {
//     mixpanelTracker.track('Wheel Spin', {
//       success,
//       prizeId: prize?.id,
//       prizeValue: prize?.value,
//       prizeDescription: prize?.description,
//       restaurantId: restaurant?.id,
//       restaurantName: restaurant?.get('name'),
//       userId: user?.id,
//       userEmail: user?.get('email') || user?.get('verifiedEmail'),
//       userName:
//         user?.get('name') ||
//         `${user?.get('firstName')} ${user?.get('lastName')}`.trim(),
//       userMobile:
//         user?.get('MobileNumber') || user?.get('verifiedMobileNumber'),
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackPrizeClaim: (
//     prize: any,
//     success: boolean,
//     restaurant: any,
//     user: any,
//     error?: string,
//   ) => {
//     mixpanelTracker.track('Prize Claim', {
//       prizeId: prize?.id,
//       prizeValue: prize?.value,
//       prizeDescription: prize?.description,
//       success,
//       error: error || null,
//       restaurantId: restaurant?.id,
//       restaurantName: restaurant?.get('name'),
//       userId: user?.id,
//       userEmail: user?.get('email') || user?.get('verifiedEmail'),
//       userName:
//         user?.get('name') ||
//         `${user?.get('firstName')} ${user?.get('lastName')}`.trim(),
//       userMobile:
//         user?.get('MobileNumber') || user?.get('verifiedMobileNumber'),
//       timestamp: new Date().toISOString(),
//     });
//   },

//   trackWheelPageView: (restaurant: any, user: any) => {
//     mixpanelTracker.track('Wheel Page View', {
//       restaurantId: restaurant?.id,
//       restaurantName: restaurant?.get('name'),
//       userId: user?.id,
//       userEmail: user?.get('email') || user?.get('verifiedEmail'),
//       userName:
//         user?.get('name') ||
//         `${user?.get('firstName')} ${user?.get('lastName')}`.trim(),
//       userMobile:
//         user?.get('MobileNumber') || user?.get('verifiedMobileNumber'),
//       timestamp: new Date().toISOString(),
//     });
//   },
// };

// // Add new type for menu events
// export const MENU_EVENTS = {
//   SECTION_CLICK: 'Menu Section Click',
//   VIEW_MENU: 'View Menu',
//   ITEM_CLICK: 'Menu Item Click',
//   ITEM_ADD_TO_CART: 'Add Item to Cart',
//   QUANTITY_CHANGE: 'Item Quantity Change',
//   OPTION_SELECT: 'Item Option Select',
//   VIEW_ORDER: 'View Order',
//   MENU_SCROLL: 'Menu Scroll',
//   IMAGE_ZOOM: 'Image Zoom',
// };

// // Helper function to format selected options
// const formatSelectedOptions = (
//   item: any,
//   selectedChoices: {[subOptionId: string]: string[]},
// ) => {
//   const formattedOptions: {
//     [key: string]: {choiceNames: string[]; choicePrices: number[]};
//   } = {};

//   item.subOptions?.forEach((subOption: any) => {
//     const choiceIds = selectedChoices[subOption.id] || [];
//     const selectedChoiceDetails = subOption.choices
//       .filter((choice: any) => choiceIds.includes(choice.id))
//       .map(
//         (choice: any): ChoiceDetail => ({
//           name: choice.name,
//           price: choice.price,
//         }),
//       );

//     if (selectedChoiceDetails.length > 0) {
//       formattedOptions[subOption.name] = {
//         choiceNames: selectedChoiceDetails.map((c: ChoiceDetail) => c.name),
//         choicePrices: selectedChoiceDetails.map((c: ChoiceDetail) => c.price),
//       };
//     }
//   });

//   return formattedOptions;
// };

// // Helper function to get base event properties
// const getBaseEventProps = (
//   restaurantId: string,
//   tableNumber: string,
//   menuId: string,
//   restaurant: any,
//   item?: any,
// ) => ({
//   restaurant_id: restaurantId,
//   table_number: tableNumber,
//   menu_id: menuId,
//   restaurant_name: restaurant?.get('name'),
//   item_id: item?.id,
//   item_name: item?.name,
//   item_price: item?.price,
//   item_category: item?.category,
// });

// export const menuAnalytics = {
//   trackSectionClick: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     sectionName: string,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.SECTION_CLICK, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant),
//       section_name: sectionName,
//     });
//   },

//   trackMenuView: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     sectionsCount: number,
//     totalItems: number,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.VIEW_MENU, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant),
//       number_of_sections: sectionsCount,
//       total_items: totalItems,
//     });
//   },

//   trackItemClick: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     item: any,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.ITEM_CLICK, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant, item),
//       is_available: item.available,
//       has_options: item.subOptions?.length > 0,
//     });
//   },

//   trackAddToCart: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     item: any,
//     quantity: number,
//     selectedOptions: any,
//     comment: string,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.ITEM_ADD_TO_CART, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant, item),
//       quantity,
//       total_price: item.price * quantity,
//       selected_options: formatSelectedOptions(item, selectedOptions),
//       has_comment: !!comment,
//       is_deal: !!item.deal,
//     });
//   },

//   trackQuantityChange: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     item: any,
//     oldQuantity: number,
//     newQuantity: number,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.QUANTITY_CHANGE, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant, item),
//       old_quantity: oldQuantity,
//       new_quantity: newQuantity,
//       is_increase: newQuantity > oldQuantity,
//     });
//   },

//   trackOptionSelect: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     item: any,
//     subOption: any,
//     choice: any,
//     isSelected: boolean,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.OPTION_SELECT, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant, item),
//       sub_option_id: subOption.id,
//       sub_option_name: subOption.name,
//       choice_id: choice.id,
//       choice_name: choice.name,
//       choice_price: choice.price,
//       is_selected: isSelected,
//     });
//   },

//   trackViewOrder: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     orderDetails: any,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.VIEW_ORDER, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant),
//       total_items: orderDetails.totalItems,
//       total_price: orderDetails.totalPrice,
//       has_pending_items: orderDetails.hasPendingItems,
//     });
//   },

//   trackMenuScroll: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     sectionName: string,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.MENU_SCROLL, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant),
//       section_name: sectionName,
//     });
//   },

//   trackImageZoom: (
//     restaurantId: string,
//     tableNumber: string,
//     menuId: string,
//     item: any,
//     restaurant: any,
//   ) => {
//     mixpanelTracker.track(MENU_EVENTS.IMAGE_ZOOM, {
//       ...getBaseEventProps(restaurantId, tableNumber, menuId, restaurant, item),
//     });
//   },
// };

export const mixpanelTracker = MixpanelTracker.getInstance();
